/**
 * Email validation and typo detection utilities
 *
 * @example
 * ```ts
 * validateEmail('test@gmial.com')
 * // { isValid: true, suggestion: 'test@gmail.com' }
 *
 * validateEmail('invalid-email')
 * // { isValid: false, error: 'Invalid email format' }
 * ```
 */

/**
 * Common domain typos mapped to correct versions
 */
const DOMAIN_TYPOS: Record<string, string> = {
  // Gmail typos
  'gmial.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.om': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmil.com': 'gmail.com',
  'gemail.com': 'gmail.com',
  'gimail.com': 'gmail.com',

  // Yahoo typos
  'yahooo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yaoo.com': 'yahoo.com',
  'yhoo.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'yhaoo.com': 'yahoo.com',

  // Outlook/Hotmail typos
  'outloo.com': 'outlook.com',
  'outlok.com': 'outlook.com',
  'outlook.con': 'outlook.com',
  'outlool.com': 'outlook.com',
  'hotmal.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotamil.com': 'hotmail.com',
  'hotmil.com': 'hotmail.com',

  // iCloud typos
  'iclod.com': 'icloud.com',
  'icould.com': 'icloud.com',
  'icloud.con': 'icloud.com',

  // AOL typos
  'aol.con': 'aol.com',
  'ao.com': 'aol.com',

  // Common TLD typos
  '.con': '.com',
  '.cpm': '.com',
  '.vom': '.com',
  '.ocm': '.com',
  '.comm': '.com',
  '.ner': '.net',
  '.nte': '.net',
  '.ogr': '.org',
};

/**
 * Validation result for emails
 */
export interface EmailValidationResult {
  isValid: boolean;
  error?: string;
  suggestion?: string;
}

/**
 * Detect if an email has a common domain typo
 * Returns the corrected email or null if no typo detected
 */
export function detectEmailTypo(email: string): string | null {
  if (!email || !email.includes('@')) return null;

  const [localPart, domain] = email.toLowerCase().split('@');
  if (!domain) return null;

  // Check exact domain matches
  if (DOMAIN_TYPOS[domain]) {
    return `${localPart}@${DOMAIN_TYPOS[domain]}`;
  }

  // Check TLD typos
  for (const [typo, correction] of Object.entries(DOMAIN_TYPOS)) {
    if (typo.startsWith('.') && domain.endsWith(typo)) {
      return `${localPart}@${domain.slice(0, -typo.length)}${correction}`;
    }
  }

  return null;
}

/**
 * Basic email format validation regex
 * Checks for: local@domain.tld format
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Disposable/temporary email domains to warn about
 */
const DISPOSABLE_DOMAINS = [
  'tempmail.com', 'throwaway.com', 'mailinator.com', 'guerrillamail.com',
  'temp-mail.org', '10minutemail.com', 'fakeinbox.com', 'trashmail.com',
];

/**
 * Validate an email address
 */
export function validateEmail(email: string): EmailValidationResult {
  // Empty is valid (field is optional)
  if (!email || email.trim() === '') {
    return { isValid: true };
  }

  const cleaned = email.trim().toLowerCase();

  // Check for spaces (common copy-paste error)
  if (email.includes(' ')) {
    return {
      isValid: false,
      error: 'Email cannot contain spaces',
    };
  }

  // Check for missing @
  if (!cleaned.includes('@')) {
    return {
      isValid: false,
      error: 'Missing @ symbol',
    };
  }

  // Check basic format
  if (!EMAIL_REGEX.test(cleaned)) {
    // Try to give a more specific error
    const [localPart, domain] = cleaned.split('@');

    if (!localPart) {
      return { isValid: false, error: 'Missing email username' };
    }
    if (!domain) {
      return { isValid: false, error: 'Missing domain after @' };
    }
    if (!domain.includes('.')) {
      return { isValid: false, error: 'Domain must include .com, .net, etc.' };
    }

    return { isValid: false, error: 'Invalid email format' };
  }

  // Check for typos (return as suggestion, not error)
  const suggestion = detectEmailTypo(cleaned);
  if (suggestion) {
    return {
      isValid: true, // Still valid, just suggesting correction
      suggestion: suggestion,
    };
  }

  // Warn about disposable emails
  const domain = cleaned.split('@')[1];
  if (DISPOSABLE_DOMAINS.some(d => domain.includes(d))) {
    return {
      isValid: true,
      suggestion: 'This appears to be a temporary email address',
    };
  }

  return { isValid: true };
}

/**
 * Clean an email (remove spaces, lowercase)
 */
export function cleanEmail(email: string): string {
  return email.trim().toLowerCase().replace(/\s/g, '');
}
